/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, PageGridContainer, Typography, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import Carousel from 'bundles/design-system/components/Carousel';
import type { CarouselSettings } from 'bundles/design-system/components/types';

const HEADSHOTS_LENGTH = 6;

const carouselSettings: (theme: Theme) => CarouselSettings = (theme: Theme) => ({
  dots: true,
  slidesToShow: 1,
  initialSlide: 0,
  slidesToScroll: 1,
  infinite: true,
  centerMode: false,
  arrows: true,
  responsive: [
    {
      breakpoint: breakpoints.values.md,
      settings: {
        arrows: false,
      },
    },
  ],
});

const styles = {
  section: css`
    background-color: var(--cds-color-grey-25);
    padding: var(--cds-spacing-1000) 0;
  `,
  headingContainer: css`
    text-align: center;
    margin-bottom: 30px;
  `,
  headingTitle: css`
    margin-bottom: 2px;
  `,
  headshotsContainer: (_: Theme, onLeft?: boolean) => css`
    padding: ${onLeft ? '0 30px 0 15px' : '0 15px 0 30px'};
    ${breakpoints.down('md')} {
      display: none;
    }
  `,
  carouselContainer: css`
    max-width: 800px;
    margin: 0 auto;
    width: 100%;

    .rc-Carousel {
      .slick-slider {
        width: auto !important;

        .slick-slide {
          padding: 35px;
          ${breakpoints.down('sm')} {
            padding: 15px;
          }
        }

        .slider-button svg {
          fill: var(--cds-color-blue-700) !important;
        }
      }
    }

    .slick-dots {
      gap: var(--cds-spacing-200);
      margin-top: var(--cds-spacing-200);
    }
  `,
};

type Props = {
  title?: string | null;
  subtitle?: string | null;
  // In order to display headshots, must have 6 items.
  headshots?: Array<React.ReactNode>;
  testimonials: Array<React.ReactNode>;
};

const TestimonialsModule = ({ title, subtitle, headshots, testimonials }: Props) => {
  const theme = useTheme();
  const invalidHeadshotLength = headshots && headshots.length !== HEADSHOTS_LENGTH;
  const invalidTestimonials = !testimonials || (testimonials && !testimonials.length);

  if (!title || invalidTestimonials || invalidHeadshotLength) {
    return null;
  }

  const leftHeadshots = headshots?.slice(0, 3);
  const rightHeadshots = headshots?.slice(3, 6);

  return (
    <section css={styles.section}>
      <PageGridContainer>
        <Grid item container xs={12} justifyContent="center" direction="column">
          <div css={styles.headingContainer}>
            <Typography variant="d2semibold" component="h2" css={styles.headingTitle}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="h2" component="p">
                {subtitle}
              </Typography>
            )}
          </div>
        </Grid>
        <Grid item container>
          <Grid item container lg={2} css={styles.headshotsContainer(theme, true)} direction="column">
            {leftHeadshots}
          </Grid>
          <Grid item lg={8} md={12} css={styles.carouselContainer}>
            <Carousel title={subtitle} enableVisibilityTracking settings={carouselSettings(theme)}>
              {testimonials}
            </Carousel>
          </Grid>
          <Grid item container lg={2} css={styles.headshotsContainer(theme)} direction="column">
            {rightHeadshots}
          </Grid>
        </Grid>
      </PageGridContainer>
    </section>
  );
};

export default TestimonialsModule;
